import React, { useState, useEffect, useRef } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { Header } from '@/components/layout'
import { Button, Modal } from '@/components/common'
import type { CompanyInfo } from '@/types'

export const SetupPage: React.FC = () => {
  const [url, setUrl] = useState('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [editForm, setEditForm] = useState<Partial<CompanyInfo>>({})
  const [showReanalyzeModal, setShowReanalyzeModal] = useState(false)
  const [reanalyzeUrl, setReanalyzeUrl] = useState('')
  const [expandedCheckId, setExpandedCheckId] = useState<string | null>(null)
  const hasFetchedRef = useRef(false)

  const companyInfo = useAppStore((state) => state.companyInfo)
  const isAnalyzing = useAppStore((state) => state.isAnalyzing)

  // Get actions from store - use getState for stable references
  const analyzeCompany = useAppStore((state) => state.analyzeCompany)
  const updateCompanyInfoFromAPI = useAppStore((state) => state.updateCompanyInfoFromAPI)

  // Fetch company info on mount - only once
  useEffect(() => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true
    useAppStore.getState().fetchCompanyInfo()
  }, [])

  const isUrlChangedInEditForm = Boolean(editForm.url?.trim()) && Boolean(companyInfo?.url?.trim())
    ? String(editForm.url).trim() !== String(companyInfo?.url).trim()
    : false

  const handleStartAnalysis = async () => {
    if (!url.trim()) return
    await analyzeCompany(url)
    setUrl('') // Clear input after successful submission
  }

  const handleReAnalysis = async () => {
    const currentUrl = String(companyInfo?.url ?? '').trim()
    setReanalyzeUrl(currentUrl)
    setShowReanalyzeModal(true)
  }

  const handleOpenEditModal = () => {
    if (companyInfo) {
      setEditForm({
        name: companyInfo.name,
        url: companyInfo.url,
        industry: companyInfo.industry || '',
        description: companyInfo.description || ''
      })
      setShowEditModal(true)
    }
  }

  const handleConfirmReanalyze = async () => {
    if (!reanalyzeUrl.trim()) return
    try {
      setShowReanalyzeModal(false)
      await analyzeCompany(reanalyzeUrl.trim())
    } catch (error) {
      console.error('Re-analysis failed:', error)
    }
  }

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.url) return

    try {
      const nextUrl = String(editForm.url).trim()
      const currentUrl = String(companyInfo?.url ?? '').trim()
      const urlChanged = Boolean(nextUrl) && nextUrl !== currentUrl

      // Close modal immediately so the user can see the analyzing state if URL changed
      setShowEditModal(false)

      if (urlChanged) {
        await analyzeCompany(nextUrl)
        // Ensure latest companyInfo is loaded before applying metadata updates
        await useAppStore.getState().fetchCompanyInfo()
      }

      // Apply name/description updates to the active company (either existing or newly analyzed)
      await updateCompanyInfoFromAPI({
        name: String(editForm.name).trim(),
        industry: editForm.industry ? String(editForm.industry).trim() : undefined,
        description: editForm.description ? String(editForm.description).trim() : undefined,
      })
    } catch (error) {
      console.error('Failed to save company info:', error)
    }
  }

  // Show URL input form if no company info
  if (!companyInfo) {
    return (
      <>
        <Header
          title="Setup"
          subtitle="Configure your company profile and run AI visibility analysis"
        />
        <div className="main-body">
          <div className="setup-input-section">
            <div className="setup-card">
              <h2 className="setup-title">Free AI Visibility Audit</h2>
              <p className="setup-subtitle">
                We'll scan your site for technical blockers and content gaps
              </p>

              <div className="setup-form">
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://yoursite.com"
                  className="setup-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleStartAnalysis()}
                />
                <Button
                  onClick={handleStartAnalysis}
                  disabled={!url.trim() || isAnalyzing}
                  loading={isAnalyzing}
                  className="setup-btn"
                >
                  {isAnalyzing ? 'Analyzing...' : 'Start Analysis'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Show loading state during analysis
  const result = companyInfo.analysisResult

  // Show loading state during analysis (or while analysis results are not yet available)
  if (isAnalyzing || !result) {
    return (
      <>
        <Header
          title="Setup"
          subtitle="Configure your company profile and run AI visibility analysis"
        />
        <div className="main-body">
          <div className="setup-loading">
            <div className="analysis-spinner" />
            {isAnalyzing ? (
              <>
                <h3>Analyzing {companyInfo.domain}...</h3>
                <p>Scanning your site for technical blockers and content gaps</p>
              </>
            ) : (
              <>
                <h3>Loading analysis results…</h3>
                <p>If this takes more than a few seconds, click “Re-analyze”.</p>
              </>
            )}
          </div>
        </div>
      </>
    )
  }

  // Show analysis results
  return (
    <>
      <Header
        title="Setup"
        subtitle="Configure your company profile and run AI visibility analysis"
        actions={
          <Button variant="secondary" onClick={handleReAnalysis}>
            <svg className="icon"><use href="#icon-refresh"></use></svg>
            Re-analyze
          </Button>
        }
      />

      <div className="main-body">
        <div className="analysis-results">
          {/* Success Banner */}
          <div className="result-banner">
            <div className="result-banner-icon">
              <svg className="icon check-icon"><use href="#icon-check"></use></svg>
            </div>
            <div className="result-banner-content">
              <h3>Analysis Complete</h3>
              <p>Your site has been scanned for AI visibility readiness</p>
            </div>
          </div>

          {/* Checklist and Company Info */}
          <div className="analysis-columns analysis-columns-two">
            {/* GEO Readiness Checklist */}
            <div className="checklist-section">
              <h4 className="section-title">
                <svg className="icon"><use href="#icon-check"></use></svg>
                GEO Readiness Checklist
              </h4>
              <div className="checklist-list">
                {result.checklist.map((item) => {
                  const isExpandable =
                    (item.id === 'schema-org' && !!result.schemaDetails) ||
                    (item.id === 'meta-descriptions' && !item.passed && !!item.details?.length)
                  const isExpanded = expandedCheckId === item.id

                  const itemClass = item.optional
                    ? 'checklist-optional'
                    : item.passed ? 'checklist-passed' : 'checklist-failed'

                  return (
                    <div key={item.id} className="checklist-group">
                      <div
                        className={`checklist-item ${itemClass} ${isExpandable ? 'checklist-expandable' : ''}`}
                        onClick={isExpandable ? () => setExpandedCheckId(isExpanded ? null : item.id) : undefined}
                      >
                        <div className="checklist-icon">
                          {item.optional ? (
                            /* Neutral info icon for optional items */
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="16" x2="12" y2="12" />
                              <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                          ) : item.passed ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          )}
                        </div>
                        <div className="checklist-content">
                          <div className="checklist-header">
                            <span className="checklist-title">{item.title}</span>
                            {!item.passed && !item.optional && item.severity && (
                              <span className={`checklist-badge checklist-badge-${item.severity}`}>
                                {item.severity.toUpperCase()}
                              </span>
                            )}
                            {item.optional && (
                              <span className="checklist-badge checklist-badge-optional">OPTIONAL</span>
                            )}
                          </div>
                          <p className="checklist-desc">{item.description}</p>
                        </div>
                        {isExpandable && (
                          <div className={`checklist-chevron ${isExpanded ? 'expanded' : ''}`}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Schema.org expandable details */}
                      {item.id === 'schema-org' && isExpanded && result.schemaDetails && (
                        <div className="checklist-expand-content">
                          <div className="schema-header">
                            <span className="schema-title">Detected Schema Types</span>
                            <span className={`schema-status schema-status-${result.schemaDetails.completeness}`}>
                              {result.schemaDetails.completeness === 'complete' ? 'Complete' :
                               result.schemaDetails.completeness === 'partial' ? 'Partial' :
                               result.schemaDetails.completeness === 'missing' ? 'Missing' : 'Unknown'}
                            </span>
                          </div>
                          <div className="schema-categories">
                            {result.schemaDetails.categories.map((cat) => (
                              <div key={cat.type} className={`schema-category-row ${cat.found ? 'found' : 'not-detected'}`}>
                                <div className="schema-category-status">
                                  {cat.found ? (
                                    <svg className="icon schema-icon-found" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  ) : (
                                    <svg className="icon schema-icon-neutral" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <line x1="6" y1="12" x2="18" y2="12" />
                                    </svg>
                                  )}
                                </div>
                                <div className="schema-category-info">
                                  <span className="schema-category-type">{cat.type}</span>
                                  <span className="schema-category-desc">
                                    {cat.description}{!cat.found && ' — Not detected'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Meta descriptions expandable details */}
                      {item.id === 'meta-descriptions' && isExpanded && (item.details?.length ?? 0) > 0 && (
                        <div className="checklist-expand-content">
                          <div className="expand-detail-list">
                            {(item.details ?? []).map((pageUrl: string, i: number) => (
                              <a
                                key={i}
                                href={pageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="expand-detail-link"
                              >
                                {pageUrl}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Company Info */}
            <div className="company-info-section">
              <div className="section-header">
                <h4 className="section-title">
                  <svg className="icon"><use href="#icon-home"></use></svg>
                  Company Info
                </h4>
                <button className="edit-btn" onClick={handleOpenEditModal}>
                  <svg className="icon"><use href="#icon-edit"></use></svg>
                  Edit
                </button>
              </div>
              <div className="company-info-card">
                <div className="company-info-row">
                  <span className="company-info-label">Company Name</span>
                  <span className="company-info-value">{companyInfo.name}</span>
                </div>
                <div className="company-info-row">
                  <span className="company-info-label">Website</span>
                  <span className="company-info-value">
                    <a href={companyInfo.url} target="_blank" rel="noopener noreferrer">
                      {companyInfo.domain}
                    </a>
                  </span>
                </div>
                {companyInfo.industry && (
                  <div className="company-info-row">
                    <span className="company-info-label">Industry</span>
                    <span className="company-info-value">{companyInfo.industry}</span>
                  </div>
                )}
                {companyInfo.description && (
                  <div className="company-info-row">
                    <span className="company-info-label">Description</span>
                    <span className="company-info-value">{companyInfo.description}</span>
                  </div>
                )}
                <div className="company-info-row">
                  <span className="company-info-label">Last Analyzed</span>
                  <span className="company-info-value">
                    {new Date(result.analyzedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Company Modal */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Company Info">
        <div className="edit-form">
          <div className="form-group">
            <label>Company Name</label>
            <input
              type="text"
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              className="form-control"
              placeholder="Your Company Name"
            />
          </div>
          <div className="form-group">
            <label>Website URL</label>
            <input
              type="text"
              value={editForm.url || ''}
              onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
              className="form-control"
              placeholder="https://yoursite.com"
            />
          </div>
          <div className="form-group">
            <label>Industry (Optional)</label>
            <input
              type="text"
              value={editForm.industry || ''}
              onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
              className="form-control"
              placeholder="e.g., SaaS, E-commerce, Healthcare"
            />
          </div>
          <div className="form-group">
            <label>Description (Optional)</label>
            <textarea
              value={editForm.description || ''}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              className="form-control"
              placeholder="Brief description of your company"
              rows={3}
            />
          </div>
        </div>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            disabled={!editForm.name || !editForm.url}
            loading={isAnalyzing}
          >
            {isUrlChangedInEditForm ? 'Save & Re-analyze' : 'Save Changes'}
          </Button>
        </div>
      </Modal>

      {/* Re-analyze Modal */}
      <Modal
        show={showReanalyzeModal}
        onClose={() => setShowReanalyzeModal(false)}
        title="Re-analyze Website"
      >
        <div className="edit-form">
          <div className="form-group">
            <label>Website URL</label>
            <input
              type="text"
              value={reanalyzeUrl}
              onChange={(e) => setReanalyzeUrl(e.target.value)}
              className="form-control"
              placeholder="https://yoursite.com"
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmReanalyze()}
            />
          </div>
        </div>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowReanalyzeModal(false)} disabled={isAnalyzing}>
            Cancel
          </Button>
          <Button onClick={handleConfirmReanalyze} disabled={!reanalyzeUrl.trim()} loading={isAnalyzing}>
            Re-analyze
          </Button>
        </div>
      </Modal>
    </>
  )
}
